<template>
    <Teleport to="body">
        <div class="assistente-juridico-container">
            <button
                v-if="!painelAberto"
                type="button"
                class="assistente-juridico-acionador btn btn-primary"
                aria-label="Abrir Assistente Jurídico"
                title="Abrir Assistente Jurídico"
                @click="abrirPainel"
            >
                <span class="material-icons-outlined" aria-hidden="true">balance</span>
                <span>Assistente Jurídico</span>
            </button>

            <section
                v-else
                class="assistente-juridico-painel"
                role="dialog"
                aria-modal="false"
                aria-labelledby="assistente-juridico-titulo"
                aria-describedby="assistente-juridico-subtitulo"
            >
                <header class="assistente-juridico-cabecalho">
                    <div class="assistente-juridico-identificacao">
                        <div class="assistente-juridico-icone" aria-hidden="true">
                            <span class="material-icons-outlined">balance</span>
                        </div>
                        <div>
                            <h2 id="assistente-juridico-titulo">Assistente Jurídico</h2>
                            <p id="assistente-juridico-subtitulo">
                                {{ subtitulo }}
                            </p>
                        </div>
                    </div>

                    <div class="assistente-juridico-acoes">
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-light"
                            :disabled="carregando"
                            aria-label="Iniciar nova conversa"
                            title="Nova conversa"
                            @click="novaConversa"
                        >
                            Nova conversa
                        </button>
                        <button
                            type="button"
                            class="assistente-juridico-fechar"
                            aria-label="Fechar Assistente Jurídico"
                            title="Fechar"
                            @click="fecharPainel"
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                </header>

                <div
                    ref="historico"
                    class="assistente-juridico-historico"
                    role="log"
                    aria-live="polite"
                    aria-relevant="additions"
                >
                    <div
                        v-for="item in mensagens"
                        :key="item.id"
                        class="assistente-juridico-mensagem"
                        :class="item.autor === 'usuario' ? 'mensagem-usuario' : 'mensagem-assistente'"
                    >
                        <span class="assistente-juridico-autor">
                            {{ item.autor === 'usuario' ? 'Você' : 'Assistente Jurídico' }}
                        </span>
                        <p>{{ item.texto }}</p>
                    </div>

                    <div
                        v-if="carregando"
                        class="assistente-juridico-mensagem mensagem-assistente assistente-juridico-loading"
                        role="status"
                    >
                        <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                        <span>{{ textoCarregamento }}</span>
                    </div>
                </div>

                <div v-if="erro" class="assistente-juridico-erro alert alert-danger" role="alert">
                    {{ erro }}
                </div>

                <form class="assistente-juridico-formulario" @submit.prevent="enviarMensagem">
                    <label class="visually-hidden" for="assistente-juridico-mensagem">
                        Mensagem para o Assistente Jurídico
                    </label>
                    <textarea
                        id="assistente-juridico-mensagem"
                        ref="campoMensagem"
                        v-model="mensagem"
                        class="form-control"
                        rows="3"
                        maxlength="3000"
                        :disabled="carregando"
                        :placeholder="placeholderMensagem"
                        aria-label="Mensagem para o Assistente Jurídico"
                        @keydown.enter.exact.prevent="enviarMensagem"
                    ></textarea>

                    <div class="assistente-juridico-rodape">
                        <small>Enter envia; Shift+Enter cria uma nova linha.</small>
                        <button
                            type="submit"
                            class="btn btn-primary btn-sm"
                            :disabled="carregando || !mensagem.trim() || !contextoValido"
                            aria-label="Enviar mensagem"
                            title="Enviar mensagem"
                        >
                            <span class="material-icons-outlined" aria-hidden="true">send</span>
                            Enviar
                        </button>
                    </div>
                </form>
            </section>
        </div>
    </Teleport>
</template>

<script>
const TELAS_VALIDAS = [
    "geral",
    "dashboard",
    "novo-processo",
    "consulta-processos",
    "movimentacao",
    "peticao",
    "prazos",
    "administracao",
];

const MENSAGEM_INICIAL_GERAL = "Olá! Posso ajudar com a navegação e com as funcionalidades disponíveis no eProc. Como posso ajudar?";
const MENSAGEM_INICIAL_PROCESSUAL = "Olá! Posso ajudar com este processo simulado, com a etapa atual ou com o uso do eProc. Como posso ajudar?";

export default {
    name: "AssistenteJuridico",
    props: {
        processoId: {
            type: [Number, String],
            default: null,
        },
        peticaoId: {
            type: [Number, String],
            default: null,
        },
        tela: {
            type: String,
            required: true,
            validator: (value) => TELAS_VALIDAS.includes(value),
        },
    },
    data() {
        return {
            BASE_URL: window.Laravel && window.Laravel.baseUrl,
            painelAberto: false,
            mensagem: "",
            mensagens: [],
            carregando: false,
            acaoAtual: null,
            erro: "",
            proximoId: 1,
            requisicaoAtual: 0,
            componenteMontado: false,
        };
    },
    computed: {
        processoIdNormalizado() {
            return this.normalizarIdOpcional(this.processoId);
        },
        peticaoIdInformadoNormalizado() {
            return this.normalizarIdOpcional(this.peticaoId);
        },
        peticaoIdNormalizado() {
            if (!this.processoValido || this.processoIdNormalizado === null) {
                return null;
            }

            return this.peticaoIdInformadoNormalizado;
        },
        telaValida() {
            return TELAS_VALIDAS.includes(this.tela);
        },
        processoValido() {
            return this.processoIdNormalizado === null
                || this.idNormalizadoValido(this.processoIdNormalizado);
        },
        peticaoValida() {
            if (this.peticaoIdInformadoNormalizado === undefined) {
                return false;
            }

            if (!this.processoValido) {
                return false;
            }

            if (this.processoIdNormalizado === null) {
                return this.peticaoIdInformadoNormalizado === null;
            }

            return this.peticaoIdNormalizado === null
                || this.idNormalizadoValido(this.peticaoIdNormalizado);
        },
        contextoValido() {
            return this.telaValida && this.processoValido && this.peticaoValida;
        },
        modoGeral() {
            return this.processoValido && this.processoIdNormalizado === null;
        },
        contextoChave() {
            if (this.modoGeral) {
                return "geral";
            }

            if (this.processoValido) {
                return `processo:${this.processoIdNormalizado}`;
            }

            return "contexto-invalido";
        },
        assinaturaVisualContexto() {
            let peticao = "null";

            if (this.peticaoIdInformadoNormalizado === undefined) {
                peticao = "invalida";
            } else if (this.peticaoIdNormalizado !== null) {
                peticao = this.peticaoIdNormalizado;
            } else if (this.peticaoIdInformadoNormalizado !== null) {
                peticao = "sem-processo";
            }

            return `${this.contextoChave}|${this.tela}|peticao:${peticao}`;
        },
        mensagemInicial() {
            return this.modoGeral
                ? MENSAGEM_INICIAL_GERAL
                : MENSAGEM_INICIAL_PROCESSUAL;
        },
        subtitulo() {
            return this.modoGeral
                ? "Apoio educacional para navegar e utilizar o eProc"
                : "Apoio educacional dentro do processo simulado";
        },
        placeholderMensagem() {
            return this.modoGeral
                ? "Digite sua pergunta sobre o uso do eProc..."
                : "Digite sua pergunta sobre o processo simulado...";
        },
        textoCarregamento() {
            return this.acaoAtual === "nova-conversa"
                ? "Iniciando nova conversa..."
                : "Pensando...";
        },
    },
    created() {
        this.reiniciarEstadoVisual();
    },
    mounted() {
        this.componenteMontado = true;
        document.addEventListener("keydown", this.tratarTeclaGlobal);
    },
    beforeUnmount() {
        this.componenteMontado = false;
        this.invalidarRequisicoes();
        document.removeEventListener("keydown", this.tratarTeclaGlobal);
    },
    watch: {
        assinaturaVisualContexto() {
            this.invalidarRequisicoes();
            this.reiniciarEstadoVisual();
        },
    },
    methods: {
        urlAssistente(caminho) {
            if (typeof this.BASE_URL !== "string" || !this.BASE_URL.trim()) {
                throw new Error("invalid_base_url");
            }

            const baseUrl = this.BASE_URL.replace(/\/+$/, "");
            const rota = String(caminho || "").replace(/^\/+/, "");

            return `${baseUrl}/${rota}`;
        },
        criarMensagemInicial() {
            return {
                id: 0,
                autor: "assistente",
                texto: this.mensagemInicial,
            };
        },
        reiniciarEstadoVisual() {
            this.mensagens = [this.criarMensagemInicial()];
            this.mensagem = "";
            this.erro = "";
            this.carregando = false;
            this.acaoAtual = null;
            this.proximoId = 1;
        },
        invalidarRequisicoes() {
            this.requisicaoAtual += 1;
        },
        iniciarRequisicao() {
            this.requisicaoAtual += 1;

            return {
                id: this.requisicaoAtual,
                assinatura: this.assinaturaVisualContexto,
            };
        },
        requisicaoAindaAtual(requisicao) {
            return this.componenteMontado
                && requisicao.id === this.requisicaoAtual
                && requisicao.assinatura === this.assinaturaVisualContexto;
        },
        normalizarIdOpcional(valor) {
            if (valor === null || valor === undefined) {
                return null;
            }

            if (typeof valor === "string") {
                const valorSemEspacos = valor.trim();

                if (valorSemEspacos === "") {
                    return null;
                }

                if (!/^\d+$/.test(valorSemEspacos)) {
                    return undefined;
                }

                valor = Number(valorSemEspacos);
            } else if (typeof valor !== "number") {
                return undefined;
            }

            return this.idNormalizadoValido(valor) ? valor : undefined;
        },
        idNormalizadoValido(valor) {
            return Number.isSafeInteger(valor) && valor > 0;
        },
        contextoProntoParaRequisicao() {
            if (this.contextoValido) {
                return true;
            }

            this.erro = "Não foi possível identificar o contexto do Assistente Jurídico.";

            return false;
        },
        abrirPainel() {
            this.painelAberto = true;
            this.erro = "";
            this.focarCampo();
            this.rolarParaFinal();
        },
        fecharPainel() {
            this.painelAberto = false;
        },
        tratarTeclaGlobal(event) {
            if (event.key === "Escape" && this.painelAberto) {
                this.fecharPainel();
            }
        },
        focarCampo() {
            this.$nextTick(() => {
                if (this.$refs.campoMensagem && !this.carregando) {
                    this.$refs.campoMensagem.focus();
                }
            });
        },
        rolarParaFinal() {
            this.$nextTick(() => {
                const historico = this.$refs.historico;

                if (historico) {
                    historico.scrollTop = historico.scrollHeight;
                }
            });
        },
        adicionarMensagem(autor, texto) {
            this.mensagens.push({
                id: this.proximoId++,
                autor,
                texto,
            });
            this.rolarParaFinal();
        },
        async enviarMensagem() {
            const texto = this.mensagem.trim();

            if (!texto || this.carregando) {
                return;
            }

            if (!this.contextoProntoParaRequisicao()) {
                return;
            }

            this.erro = "";
            this.mensagem = "";
            this.adicionarMensagem("usuario", texto);
            this.carregando = true;
            this.acaoAtual = "mensagem";
            this.rolarParaFinal();
            const requisicao = this.iniciarRequisicao();

            try {
                const response = await window.axios.post(this.urlAssistente("assistente-juridico/mensagem"), {
                    mensagem: texto,
                    processo_id: this.processoIdNormalizado,
                    peticao_id: this.peticaoIdNormalizado,
                    tela: this.tela,
                });

                if (!this.requisicaoAindaAtual(requisicao)) {
                    return;
                }

                const resposta = response && response.data && response.data.mensagem;

                if (response.data.status !== "ok" || typeof resposta !== "string" || !resposta.trim()) {
                    throw new Error("invalid_assistant_response");
                }

                this.adicionarMensagem("assistente", resposta.trim());
            } catch (error) {
                if (this.requisicaoAindaAtual(requisicao)) {
                    this.erro = this.mensagemErro(error);
                }
            } finally {
                if (this.requisicaoAindaAtual(requisicao)) {
                    this.carregando = false;
                    this.acaoAtual = null;
                    this.rolarParaFinal();
                    this.focarCampo();
                }
            }
        },
        async novaConversa() {
            if (this.carregando) {
                return;
            }

            if (!this.contextoProntoParaRequisicao()) {
                return;
            }

            const confirmado = window.confirm(
                "Deseja iniciar uma nova conversa? O histórico exibido neste painel será limpo."
            );

            if (!confirmado) {
                return;
            }

            this.erro = "";
            this.carregando = true;
            this.acaoAtual = "nova-conversa";
            this.rolarParaFinal();
            const requisicao = this.iniciarRequisicao();

            try {
                const response = await window.axios.post(this.urlAssistente("assistente-juridico/nova-conversa"), {
                    processo_id: this.processoIdNormalizado,
                });

                if (!this.requisicaoAindaAtual(requisicao)) {
                    return;
                }

                if (!response.data || response.data.status !== "ok") {
                    throw new Error("invalid_new_conversation_response");
                }

                this.reiniciarEstadoVisual();
            } catch (error) {
                if (this.requisicaoAindaAtual(requisicao)) {
                    this.erro = this.mensagemErro(error);
                }
            } finally {
                if (this.requisicaoAindaAtual(requisicao)) {
                    this.carregando = false;
                    this.acaoAtual = null;
                    this.rolarParaFinal();
                    this.focarCampo();
                }
            }
        },
        mensagemErro(error) {
            const status = error && error.response ? error.response.status : null;

            if (status === 403) {
                return "O Assistente Jurídico não está disponível.";
            }

            if (status === 422) {
                return "Confira a mensagem enviada.";
            }

            if (status === 429) {
                return "Muitas mensagens foram enviadas. Aguarde um momento.";
            }

            if (status === 503 || status === 504) {
                return "O Assistente Jurídico está temporariamente indisponível.";
            }

            return "Não foi possível obter uma resposta agora.";
        },
    },
};
</script>

<style scoped>
.assistente-juridico-container {
    position: fixed;
    right: 1.25rem;
    bottom: 1.25rem;
    z-index: 1040;
}

.assistente-juridico-acionador {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-height: 48px;
    padding: 0.65rem 1rem;
    border-radius: 999px;
    box-shadow: 0 0.4rem 1rem rgba(0, 0, 0, 0.22);
    font-weight: 600;
}

.assistente-juridico-acionador .material-icons-outlined {
    font-size: 1.3rem;
}

.assistente-juridico-painel {
    display: flex;
    flex-direction: column;
    width: 380px;
    max-width: calc(100vw - 2rem);
    height: min(620px, 75vh);
    min-height: 420px;
    overflow: hidden;
    border: 1px solid #ced4da;
    border-radius: 0.75rem;
    background: #fff;
    box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.25);
}

.assistente-juridico-cabecalho {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.85rem;
    color: #fff;
    background: #0d6efd;
}

.assistente-juridico-identificacao {
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    min-width: 0;
}

.assistente-juridico-icone {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
}

.assistente-juridico-cabecalho h2 {
    margin: 0;
    font-size: 1rem;
    line-height: 1.3;
}

.assistente-juridico-cabecalho p {
    margin: 0.15rem 0 0;
    font-size: 0.72rem;
    line-height: 1.35;
    opacity: 0.9;
}

.assistente-juridico-acoes {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.4rem;
}

.assistente-juridico-acoes .btn {
    font-size: 0.72rem;
    white-space: nowrap;
}

.assistente-juridico-fechar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 0;
    border-radius: 0.3rem;
    color: #fff;
    background: transparent;
    font-size: 1.5rem;
    line-height: 1;
}

.assistente-juridico-fechar:hover,
.assistente-juridico-fechar:focus {
    background: rgba(255, 255, 255, 0.18);
    outline: 2px solid rgba(255, 255, 255, 0.7);
    outline-offset: 1px;
}

.assistente-juridico-historico {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 0.7rem;
    min-height: 0;
    padding: 0.85rem;
    overflow-y: auto;
    background: #f7f9fb;
}

.assistente-juridico-mensagem {
    max-width: 86%;
    padding: 0.65rem 0.75rem;
    border-radius: 0.65rem;
    font-size: 0.86rem;
    line-height: 1.45;
    overflow-wrap: anywhere;
}

.assistente-juridico-mensagem p {
    margin: 0.2rem 0 0;
    white-space: pre-wrap;
}

.assistente-juridico-autor {
    display: block;
    font-size: 0.68rem;
    font-weight: 700;
}

.mensagem-assistente {
    align-self: flex-start;
    border: 1px solid #dee2e6;
    color: #212529;
    background: #fff;
}

.mensagem-usuario {
    align-self: flex-end;
    color: #fff;
    background: #0d6efd;
}

.assistente-juridico-loading {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #495057;
}

.assistente-juridico-erro {
    flex: 0 0 auto;
    margin: 0;
    padding: 0.55rem 0.75rem;
    border-right: 0;
    border-left: 0;
    border-radius: 0;
    font-size: 0.8rem;
}

.assistente-juridico-formulario {
    flex: 0 0 auto;
    padding: 0.75rem;
    border-top: 1px solid #dee2e6;
    background: #fff;
}

.assistente-juridico-formulario textarea {
    min-height: 72px;
    max-height: 150px;
    resize: vertical;
    font-size: 0.86rem;
}

.assistente-juridico-rodape {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-top: 0.55rem;
}

.assistente-juridico-rodape small {
    color: #6c757d;
    font-size: 0.68rem;
}

.assistente-juridico-rodape .btn {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
}

.assistente-juridico-rodape .material-icons-outlined {
    font-size: 1rem;
}

@media (max-width: 575.98px) {
    .assistente-juridico-container {
        right: 0.5rem;
        bottom: 0.5rem;
        left: 0.5rem;
    }

    .assistente-juridico-acionador {
        float: right;
    }

    .assistente-juridico-painel {
        width: 100%;
        max-width: none;
        height: min(680px, calc(100vh - 1rem));
        min-height: 360px;
    }

    .assistente-juridico-cabecalho {
        padding: 0.7rem;
    }

    .assistente-juridico-icone {
        display: none;
    }

    .assistente-juridico-acoes {
        gap: 0.2rem;
    }

    .assistente-juridico-acoes .btn {
        padding-right: 0.4rem;
        padding-left: 0.4rem;
    }
}
</style>
